/**
 * 수집 결과 › 오브젝트별 읽기량 — 한 오브젝트를 한 줄에 두 막대로(2026-10-14).
 *
 * 예전에는 「오브젝트별 비용」 이라는 제목의 가로 막대그래프 두 장(캐시읽기 블록 · 물리읽기 MB)이었다.
 * 두 값은 비용(옵티마이저 COST)이 아니라 **읽은 양**이고, 두 장이 따로 그려져 같은 오브젝트를 눈으로 짝지어야 했다.
 *   · 버퍼 읽기 = 블록 수 — DBMS_XPLAN 의 Buffers(자식 포함 누적)라 줄마다 「부모 − 자식 합계」 로 자체값을 구해 합친다(planTreeSelf)
 *   · 물리 읽기 = 요청 횟수 — GV$SQL_PLAN_MONITOR 의 PHYSICAL_READ_REQUESTS(그 줄이 직접 한 일)를 그대로 합친다.
 *     요청 한 번이 여러 블록을 읽을 수 있어 블록 수와 견줄 수 없다 — 그래서 막대는 열마다 따로 100% 다.
 * 두 소스의 기준 실행이 다를 수 있다(버퍼 = 커서의 마지막 실행, 물리 = 이 수집이 고정한 실행) — 머리글 설명이 말한다.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { byObject } from '../../utils/planTreeSelf';

const fmt = (v) => (v == null ? '-' : Math.round(v).toLocaleString());

export function mergeObjectReads(lines, otherLabel, topN = 12) {
    const buf = byObject(lines, { field: 'cache_reads', cumulative: true, otherLabel, topN: 100 });
    const phy = byObject(lines, { field: 'physical_reads', cumulative: false, otherLabel, topN: 100 });
    const map = new Map();
    buf.forEach(r => map.set(r.name, { name: r.name, buffer: r.value, physical: null }));
    phy.forEach(r => {
        const cur = map.get(r.name) || { name: r.name, buffer: null, physical: null };
        cur.physical = r.value;
        map.set(r.name, cur);
    });
    const rows = [...map.values()]
        .sort((a, b) => ((b.buffer || 0) - (a.buffer || 0)) || ((b.physical || 0) - (a.physical || 0)))
        .slice(0, topN);
    const sum = (k) => [...map.values()].reduce((a, r) => a + (r[k] || 0), 0);
    return { rows, totalBuffer: sum('buffer'), totalPhysical: sum('physical') };
}

export default function ObjectReadsList({ lines }) {
    const { t } = useTranslation();
    const otherLabel = t('monitoring:anal.objChart.otherOps');
    const { rows, totalBuffer, totalPhysical } = useMemo(() => mergeObjectReads(lines || [], otherLabel), [lines, otherLabel]);
    if (rows.length === 0) return null;
    const maxB = Math.max(1, ...rows.map(r => r.buffer || 0));
    const maxP = Math.max(1, ...rows.map(r => r.physical || 0));
    const pct = (v, tot) => (v == null || !tot ? '' : `${Math.round((v / tot) * 100)}%`);
    return (
        <table className="analysis-objreads" aria-label={t('monitoring:anal.objChart.title')}>
            <thead>
                <tr>
                    <th>{t('monitoring:anal.objChart.object')}</th>
                    <th title={t('monitoring:anal.objChart.cacheHint')}>
                        {t('monitoring:anal.objChart.cacheReads')}<i className="analysis-col-hint" aria-hidden="true">?</i>
                    </th>
                    <th title={t('monitoring:anal.objChart.physHint')}>
                        {t('monitoring:anal.objChart.physReqs')}<i className="analysis-col-hint" aria-hidden="true">?</i>
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.map(r => (
                    <tr key={r.name}>
                        <td className="analysis-objreads-name" title={r.name}>{r.name}</td>
                        <td>
                            <span className="analysis-objreads-cell">
                                <span className="analysis-objreads-bar is-buffer" style={{ width: `${((r.buffer || 0) / maxB) * 100}%` }} />
                                <span className="analysis-objreads-val">{fmt(r.buffer)}</span>
                                <span className="analysis-objreads-pct">{pct(r.buffer, totalBuffer)}</span>
                            </span>
                        </td>
                        <td>
                            <span className="analysis-objreads-cell">
                                <span className="analysis-objreads-bar is-physical" style={{ width: `${((r.physical || 0) / maxP) * 100}%` }} />
                                <span className="analysis-objreads-val">{fmt(r.physical)}</span>
                                <span className="analysis-objreads-pct">{pct(r.physical, totalPhysical)}</span>
                            </span>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
